import { GA4Item, getSizeFromAttrs, stripSpecialChars } from 'helpers/analytics/ga4Events';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { hasVariantPrice } from 'helpers/utils/variantEligibility';
import { getAttributeLabel } from 'helpers/utils/attributes';
import { Money, ProductPrices, Variant } from 'types/entity/product';

export const formatMoney = (m?: Money, locale = 'en-US'): string => {
  if (typeof m?.centAmount !== 'number' || !Number.isFinite(m.centAmount)) return '';

  return CurrencyHelpers.formatForCurrency(m, locale);
};

export const getDiscountDisplay = (price?: ProductPrices) => {
  const base = price?.value;
  const disc = price?.discounted?.discount?.obj?.value;
  const discountedMoney = price?.discounted?.value;
  if (!base || !disc) {
    return { type: 'none' as const, label: '', amountLabel: '', percentage: 0 };
  }

  if (disc.type === 'absolute') {
    const { money = [] } = disc;
    const absoluteMoney = money.find(({ currencyCode }) => currencyCode === base.currencyCode) ?? money[0];

    if (!absoluteMoney) {
      return { type: 'none' as const, label: '', amountLabel: '', percentage: 0 };
    }

    const amountLabel = formatMoney(absoluteMoney);

    const percentageCalculation = (base?.centAmount ?? 0) - (discountedMoney?.centAmount ?? 0);
    const percentage = percentageCalculation / 100;
    return {
      type: 'absolute' as const,
      amountLabel,
      percentage,
    };
  }

  if (disc.type === 'relative') {
    const { permyriad } = disc;

    const percentage = base?.centAmount ? permyriad / 100 : 0;

    return {
      type: 'relative' as const,
      percentage,
    };
  }

  return { type: 'none' as const, label: '', percentage: 0 };
};

export const discountTypeHelper = (price: ProductPrices | undefined, locale: string) => {
  const discounted = price?.discounted?.value;
  const baseLabel = formatMoney(price?.value, locale);
  const hasDiscount = typeof discounted?.centAmount === 'number';
  const finalLabel = hasDiscount ? formatMoney(discounted, locale) : baseLabel;

  const discountInfo = getDiscountDisplay(price);
  return {
    baseLabel,
    finalLabel,
    hasDiscount,
    discountType: discountInfo?.type,
    discountAmountLabel: discountInfo.amountLabel,
    discountPercentage: Number(discountInfo?.percentage?.toFixed(2)),
  };
};

export const centsToDecimal = (centAmount?: number, fractionDigits = 2): number | undefined => {
  if (centAmount === undefined || centAmount === null) return undefined;
  return Number((centAmount / Math.pow(10, fractionDigits)).toFixed(fractionDigits));
};

export const applyGtmAttributes = (
  enableGA4: boolean,
  variant: Variant | undefined,
  attrs: any,
  attributesToDisplay: any,
) => {
  const item: GA4Item = {};

  const primaryAttr = attributesToDisplay?.primaryAttribute;
  const secondaryAttr = attributesToDisplay?.secondaryAttribute;

  const size = getSizeFromAttrs(attrs) ?? (primaryAttr ? getAttributeLabel(variant?.attributes?.[primaryAttr]) : undefined);
  if (size) item.item_size = size;

  const colorFromAttr = secondaryAttr ? getAttributeLabel(variant?.attributes?.[secondaryAttr]) : undefined;
  const color = colorFromAttr ?? attrs.design_color_universal ?? attrs.color ?? attrs.item_color;
  if (color) item.item_color = stripSpecialChars(color);

  const brand = attrs.brand_universal ?? attrs.brand;
  if (brand) item.item_brand = stripSpecialChars(brand);

  const material =
    getAttributeLabel(attrs, 'material_dd') ||
    getAttributeLabel(attrs, 'material_cm') ||
    getAttributeLabel(attrs, 'material_wa') ||
    getAttributeLabel(attrs, 'material_textiles') ||
    getAttributeLabel(attrs, 'material_ornaments') ||
    getAttributeLabel(attrs, 'material') ||
    getAttributeLabel(attrs, 'item_material');

  if (material) item.item_material = stripSpecialChars(material);

  // trim
  const trim = getAttributeLabel(variant?.attributes, 'trim_cards');
  if (enableGA4 && trim) item.item_trim = String(trim);

  //Images
  const itemImage = variant?.images?.[0];
  if (enableGA4 && itemImage) item.item_img_url = itemImage;

  // Border
  const LineItemBorder = variant?.attributes?.borderless_pp;
  if (enableGA4 && LineItemBorder) item.item_border = LineItemBorder;

  // Has Stand
  const hasStand = variant?.attributes?.has_stand_wa;
  if (enableGA4 && hasStand) item.item_stand = hasStand;

  // Mat
  const hasMat = getAttributeLabel(variant?.attributes, 'mat_included_wa');
  if (enableGA4 && hasMat) item.item_mat = hasMat;

  // Shape
  const itemShape = getAttributeLabel(variant?.attributes, 'product_shape_ornaments');
  if (enableGA4 && itemShape) item.item_shape = itemShape;

  // Pack Qty
  const packQty = getAttributeLabel(variant?.attributes, 'pack_size_cards');
  if (enableGA4 && packQty) item.item_pack_qty = packQty;

  // item_envelope_printing
  const envelopPrint = getAttributeLabel(variant?.attributes, 'envelope_cards') || variant?.attributes?.envelope_included_cards;
  if (enableGA4 && envelopPrint) item.item_envelope_printing = envelopPrint;

  // Cover Type
  const coverType = getAttributeLabel(variant?.attributes, 'cover_type_pb');
  if (enableGA4 && coverType) item.item_cover_type = coverType;

  // Cover Type
  const calanderDate = getAttributeLabel(variant?.attributes, 'starting_month');
  if (enableGA4 && calanderDate) item.item_calendar_start_date = calanderDate;

  // // Cover Type
  // const backDesign = variant?.attributes?.designer_universal || variant?.attributes?.agency_design_id_universal || variant?.attributes?.design_name_universal
  // if( enableGA4 && backDesign) item.item_back_design = backDesign;

  if (variant?.discountedPrice?.value?.centAmount !== undefined && hasVariantPrice(variant)) {
    const discountAmt = variant.price.centAmount - variant.discountedPrice.value.centAmount;
    if (discountAmt > 0) {
      item.discount = centsToDecimal(discountAmt, variant.price.fractionDigits);
    }
  }

  return item;
};

export default {
  formatMoney,
  getDiscountDisplay,
  discountTypeHelper,
  centsToDecimal,
};
